import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import supabase from '../../../../../supabaseClient';

const STATUS_META = {
  ACTIVE: { label: 'Aktywne', color: '#3E6B5C', bg: '#e7f0ec' },
  SCHEDULED: { label: 'Zaplanowane', color: '#4A6B8A', bg: '#e9eef4' },
  ENDED: { label: 'Zakończone', color: '#8a8080', bg: '#f0ecec' },
};

const TARGET_OPTIONS = [
  { value: 'worker', label: 'Pracownicy' },
  { value: 'driver', label: 'Kierowcy' },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function statusOf(item, today) {
  if (item.date_to < today) return 'ENDED';
  if (item.date_from > today) return 'SCHEDULED';
  return 'ACTIVE';
}

async function fetchAnnouncements(idConfig) {
  const { data, error } = await supabase
    .from('noticeboard')
    .select(`
      id, content, date_from, date_to, target_profile_types, created_at,
      author:profiles!noticeboard_created_by_fkey ( id, full_name )
    `)
    .eq('id_configuration', idConfig)
    .order('date_from', { ascending: false });
  if (error) throw error;
  return data;
}

function Noticeboard() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [idConfig, setIdConfig] = useState(null);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data } = await supabase.from('profiles').select('id_configuration').eq('id', session.user.id).single();
        if (data) setIdConfig(data.id_configuration);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (!idConfig) return;
    let cancelled = false;
    setLoading(true);
    fetchAnnouncements(idConfig)
      .then((data) => { if (!cancelled) setAnnouncements(data); })
      .catch((err) => console.error('Błąd pobierania ogłoszeń:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshVersion, idConfig]);

  const today = todayISO();

  const filteredAnnouncements = useMemo(() => {
    if (filterStatus === 'ALL') return announcements;
    return announcements.filter((a) => statusOf(a, today) === filterStatus);
  }, [announcements, filterStatus, today]);

  async function handleRemove(id) {
    const { error } = await supabase.from('noticeboard').delete().eq('id', id).eq('id_configuration', idConfig);
    if (error) { console.error('Błąd usuwania ogłoszenia:', error); return; }
    setRefreshVersion((v) => v + 1);
  }

  return (
    <Box sx={{ mt: 5, mx: 'auto', px: { xs: 2, md: 4 }, maxWidth: 1400 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <div style={{ fontSize: 32, fontWeight: 900, color: '#6B1D2F', letterSpacing: '-0.5px' }}>Tablica ogłoszeń</div>
        <button onClick={() => { setEditingItem(null); setModalOpen(true); }} style={filledBtnStyle}>+ Nowe ogłoszenie</button>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px', padding: '4px', mb: 3, width: 'fit-content' }}>
        {[
          { key: 'ALL', label: 'Wszystkie' },
          { key: 'ACTIVE', label: 'Aktywne' },
          { key: 'SCHEDULED', label: 'Zaplanowane' },
          { key: 'ENDED', label: 'Zakończone' },
        ].map((tab) => (
          <button key={tab.key} onClick={() => setFilterStatus(tab.key)} style={{
            border: 'none', padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            background: filterStatus === tab.key ? '#6B1D2F' : 'transparent',
            color: filterStatus === tab.key ? '#fff' : '#705A5E',
          }}>
            {tab.label}
          </button>
        ))}
      </Box>

      {loading && <div style={{ fontSize: 12, color: '#705A5E' }}>Ładowanie…</div>}

      {!loading && filteredAnnouncements.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#705A5E', fontStyle: 'italic', background: '#fff', border: '1px solid #eee', borderRadius: 16 }}>
          Brak ogłoszeń w tej kategorii.
        </div>
      )}

      {filteredAnnouncements.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filteredAnnouncements.map((item) => {
            const st = statusOf(item, today);
            const meta = STATUS_META[st];
            const periodLabel = item.date_from === item.date_to ? item.date_from : `${item.date_from} → ${item.date_to}`;
            return (
              <div key={item.id} style={{
                background: '#fff', border: '1px solid #eee', borderRadius: 16, boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
                padding: '20px 24px', display: 'flex', gap: 18, alignItems: 'flex-start',
              }}>
                <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 3, background: meta.color }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase',
                      color: meta.color, background: meta.bg, padding: '3px 9px', borderRadius: 20,
                    }}>
                      {meta.label}
                    </span>
                    <span style={{ fontSize: 12, color: '#705A5E' }}>{periodLabel}</span>
                  </div>
                  <div style={{ fontSize: 15, color: '#2C1419', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{item.content}</div>
                  <div style={{ fontSize: 12, color: '#a89a9a', marginTop: 10 }}>Dodał: {item.author?.full_name || '—'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => { setEditingItem(item); setModalOpen(true); }} style={{ background: 'none', border: 'none', color: '#4A6B8A', fontSize: 12, cursor: 'pointer' }}>
                    Edytuj
                  </button>
                  <button onClick={() => handleRemove(item.id)} style={{ background: 'none', border: 'none', color: '#c0392b', fontSize: 12, cursor: 'pointer' }}>
                    Usuń
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <NoticeboardAddModal
          editItem={editingItem}
          onClose={() => { setModalOpen(false); setEditingItem(null); setRefreshVersion((v) => v + 1); }}
        />
      )}
    </Box>
  );
}

function NoticeboardAddModal({ onClose, editItem }) {
  const isEditing = Boolean(editItem);
  const [idConfig,    setIdConfig]    = useState(null);
  const [content, setContent] = useState(editItem?.content || '');
  const [dateFrom, setDateFrom] = useState(editItem?.date_from || todayISO());
  const [dateTo, setDateTo] = useState(editItem?.date_to || todayISO());
  const [targets, setTargets] = useState(editItem?.target_profile_types || []);
  const [currentManagerId, setCurrentManagerId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);


   // Init
  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setCurrentManagerId(session.user.id);
        const { data } = await supabase.from('profiles').select('id_configuration').eq('id', session.user.id).single();
        if (data) setIdConfig(data.id_configuration);
      }
    };
    init();
  }, []);

  function toggleTarget(value) {
    setTargets((prev) => prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]);
  }

  async function handleSave() {
    const trimmed = content.trim();
    if (!trimmed) { setError('Treść ogłoszenia jest wymagana.'); return; }
    if (dateTo < dateFrom) { setError('Data "do" nie może być wcześniejsza niż data "od".'); return; }
    if (targets.length === 0) { setError('Wybierz przynajmniej jedną grupę odbiorców.'); return; }
    if (!currentManagerId) { setError('Nie udało się ustalić zalogowanego managera — zaloguj się ponownie.'); return; }

    setSaving(true);
    setError(null);

    if (isEditing) {
      const { error: updateError } = await supabase.from('noticeboard').update({
        content: trimmed,
        date_from: dateFrom,
        date_to: dateTo,
        target_profile_types: targets,
      }).eq('id', editItem.id);
      setSaving(false);

      if (updateError) {
        console.error('Błąd edycji ogłoszenia:', updateError);
        setError('Nie udało się zapisać zmian.');
        return;
      }
      onClose();
      return;
    }

    const { error: insertError } = await supabase.from('noticeboard').insert({
      content: trimmed,
      date_from: dateFrom,
      date_to: dateTo,
      target_profile_types: targets,
      created_by: currentManagerId,
      id_configuration: idConfig
    });
    setSaving(false);

    if (insertError) {
      console.error('Błąd dodawania ogłoszenia:', insertError);
      setError('Nie udało się opublikować ogłoszenia.');
      return;
    }
    onClose();
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(44,20,25,0.45)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 28, width: 460, maxWidth: '90vw' }}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 16, color: '#6B1D2F' }}>{isEditing ? 'Edycja ogłoszenia' : 'Nowe ogłoszenie'}</div>

        <label style={labelStyle}>Treść ogłoszenia</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Wpisz treść ogłoszenia..."
          style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
        />

        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Publikacja od</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Publikacja do</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <label style={labelStyle}>Widoczne dla</label>
        <div style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
          {TARGET_OPTIONS.map((opt) => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#2C1419', cursor: 'pointer' }}>
              <input type="checkbox" checked={targets.includes(opt.value)} onChange={() => toggleTarget(opt.value)} />
              {opt.label}
            </label>
          ))}
        </div>

        {error && <div style={{ color: '#c0392b', fontSize: 12, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #e3d9d9', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#705A5E', cursor: 'pointer' }}>
            Anuluj
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            background: '#6B1D2F', border: 'none', padding: '9px 18px', borderRadius: 8,
            fontSize: 13, fontWeight: 700, color: '#fff', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
          }}>
            {saving ? (isEditing ? 'Zapisywanie…' : 'Publikowanie…') : (isEditing ? 'Zapisz zmiany' : 'Opublikuj')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Noticeboard;

const labelStyle = { display: 'block', fontSize: 12, color: '#705A5E', marginBottom: 4 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 10px', border: '1px solid #e3d9d9', borderRadius: 8, fontSize: 14, marginBottom: 14 };
const filledBtnStyle = { background: '#6B1D2F', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, padding: '9px 18px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' };